import React, { useState } from "react";
import { ListMusic, PlusCircle, Heart, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { usePartyStore } from "../stores/partyStore";
import { PartyHeader } from "../components/party/PartyHeader";
import { PartyMembersList } from "../components/party/PartyMembersList";
import { SortableQueueList } from "../components/queue/SortableQueueList";
import { SearchBar } from "../components/search/SearchBar";
import { SearchResultList } from "../components/search/SearchResultList";
import { FavoritesList } from "../components/playlist/FavoritesList";
import { searchYoutube } from "../lib/firebase";
import type { YouTubeSearchResult } from "../types";

export const PartyControllerView: React.FC = () => {
  const { activeTab, setActiveTab, queue } = usePartyStore();
  const [searchResults, setSearchResults] = useState<YouTubeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSearchHelp, setShowSearchHelp] = useState(false);

  const handleSearch = async (queryText: string) => {
    setSearching(true);
    try {
      const items = await searchYoutube(queryText);
      setSearchResults(items || []);
    } catch (err: any) {
      alert(`Search failed: ${err.message}`);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6 animate-fade-in">
      {/* Party Status Header */}
      <PartyHeader />

      {/* Realtime Members Row */}
      <PartyMembersList />

      {/* Navigation Tabs */}
      <div className="flex bg-card p-1.5 rounded-2xl border border-border shadow-md">
        <button
          onClick={() => setActiveTab("queue")}
          className={`flex-1 py-3 px-3 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer ${
            activeTab === "queue"
              ? "bg-primary text-white shadow-md shadow-primary/25"
              : "text-text-muted hover:text-text-main hover:bg-background/50"
          }`}
        >
          <ListMusic className="w-4 h-4" />
          <span>Queue</span>
          {queue.length > 0 && (
            <span
              className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-full ${
                activeTab === "queue"
                  ? "bg-white/25 text-white"
                  : "bg-border text-text-main"
              }`}
            >
              {queue.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveTab("search")}
          className={`flex-1 py-3 px-3 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer ${
            activeTab === "search"
              ? "bg-primary text-white shadow-md shadow-primary/25"
              : "text-text-muted hover:text-text-main hover:bg-background/50"
          }`}
        >
          <PlusCircle className="w-4 h-4" />
          <span>Add a Song</span>
        </button>

        <button
          onClick={() => setActiveTab("favorites")}
          className={`flex-1 py-3 px-3 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer ${
            activeTab === "favorites"
              ? "bg-primary text-white shadow-md shadow-primary/25"
              : "text-text-muted hover:text-text-main hover:bg-background/50"
          }`}
        >
          <Heart className="w-4 h-4" />
          <span>My Playlist</span>
        </button>
      </div>

      {/* Tab Panels */}
      <div className="pt-2">
        {activeTab === "queue" && <SortableQueueList />}

        {activeTab === "search" && (
          <div className="space-y-4">
            <SearchBar onSearch={handleSearch} loading={searching} />

            {/* Search Tips Expando */}
            <div className="bg-card border border-border rounded-2xl p-3.5 text-xs shadow-sm">
              <button
                type="button"
                onClick={() => setShowSearchHelp(!showSearchHelp)}
                className="w-full flex items-center justify-between text-text-muted hover:text-text-main transition-colors cursor-pointer"
              >
                <span className="font-semibold flex items-center gap-1.5 text-text-main">
                  <HelpCircle className="w-4 h-4 text-accent" />
                  Search Tips & Keyboard Shortcuts
                </span>
                {showSearchHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {showSearchHelp && (
                <ul className="mt-2 space-y-1.5 text-text-muted list-disc list-inside pt-2 border-t border-border text-[11px]">
                  <li>Enter artist or song name — &ldquo;karaoke&rdquo; is added automatically.</li>
                  <li>Tap the <strong>+</strong> button to add a song to the live party queue.</li>
                  <li>Tap the <strong>heart</strong> icon to save songs to your personal go-to playlist.</li>
                </ul>
              )}
            </div>

            <SearchResultList results={searchResults} loading={searching} />
          </div>
        )}

        {activeTab === "favorites" && <FavoritesList />}
      </div>
    </div>
  );
};
